import React, { createContext, useContext, useDebugValue } from "react";
import { emptyEventManager } from "../event-manager/createEventManager";
/**
 * ObservedBits functionality is not public API and can break in the future
 * Once/if that happens, it's hopefully replaced by functionality
 * that can achieve the same purpose without the same limitations
 * Using unstable internal react API, ref: https://github.com/facebook/react/issues/14815#issuecomment-462194923
 *
 */
function useUnstableContextWithoutWarning(Context, observedBits) {
    const { ReactCurrentDispatcher } = React
        .__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
    const dispatcher = ReactCurrentDispatcher.current;
    if (!dispatcher) {
        throw new Error("Hooks can only be called inside the body of a function component. (https://fb.me/react-invalid-hook-call)");
    }
    return dispatcher.useContext(Context, observedBits);
}
export const dispatchContext = createContext(() => { });
dispatchContext.displayName = "CalculationDispatch";
export const DispatchProvider = dispatchContext.Provider;
export const serviceManagerContext = createContext({
    call: () => Promise.reject("Service manager missing"),
    trigger: () => Promise.reject("Service manager missing"),
    getHref: () => undefined
});
serviceManagerContext.displayName = "ServiceManager";
export const ServiceManagerProvider = serviceManagerContext.Provider;
const calculationObserveFlags = {
    values: 1 << 0,
    isMissing: 1 << 1,
    isWriteLocked: 1 << 2,
    isFieldVisible: 1 << 3,
    isEnabled: 1 << 4,
    errors: 1 << 5,
    formValues: 1 << 6,
    controlFieldMaps: 1 << 7,
    fieldControlMaps: 1 << 8,
    resolvedVariableMaps: 1 << 9,
    serviceBodyValuesMap: 1 << 10,
    variableControlMaps: 1 << 11,
    initialFormValues: 1 << 12,
    defaultFieldVisibility: 1 << 13,
    defaultFieldEnabled: 1 << 14
};
const calculateChangedBits = (prev, next) => Object.entries(calculationObserveFlags).reduce((res, [key, bit]) => (prev[key] !== next[key] ? res | bit : res), 0);
const defaultState = {};
export const calculationContext = createContext(defaultState, calculateChangedBits);
calculationContext.displayName = "CalculationState";
export const CalculationProvider = calculationContext.Provider;
export const useHasCalculation = () => {
    const state = useUnstableContextWithoutWarning(calculationContext, 0);
    return state !== defaultState;
};
export const useCalculation = (...observedKeys) => {
    const flags = observedKeys.length > 0
        ? observedKeys.reduce((res, key) => res | calculationObserveFlags[key], 0)
        : undefined;
    const state = useUnstableContextWithoutWarning(calculationContext, flags);
    useDebugValue(`Observing: ${observedKeys.length > 0 ? observedKeys.join(", ") : "calculation"}`);
    return Object.fromEntries(Object.entries(state).filter(([key]) => observedKeys.includes(key)));
};
export const eventContext = createContext(emptyEventManager);
export const useEventManager = () => {
    const state = useContext(eventContext);
    // useDebugValue(state ? "patchManager" : "no patchManager");
    return state;
};
export const EventProvider = eventContext.Provider;
//# sourceMappingURL=context.js.map